import React from 'react';
import { toast } from 'react-toastify';
import { FaInfoCircle } from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { IoCopy, IoCheckboxOutline } from 'react-icons/io5';
import { Datum } from '../../interfaces';
import Dropdown from '../common/Dropdown';

interface Props {
  wrapperClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  className?: string;
  label?: string;
  id: string;
  name: string;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  readOnly?: boolean;
  hiddenLabel?: boolean;
  multiline?: boolean;
  helperText?: string;
  value?: any;
  rows?: number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  customChange?: Function;
  error?: Datum;
  appendStyles?: boolean;
  showCopy?: boolean;
  inputHeight?: boolean;
}

interface CopyButtonProps {
  multiline?: boolean;
  value: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({ multiline, value }) => {
  const [copied, setCopied] = React.useState<boolean>(false);

  const copy = () => {
    navigator?.clipboard?.writeText(value);
    toast(
      ({ closeToast }) => <div>item copied to clipboard</div>,
      {
        type: 'success',
        hideProgressBar: true,
        position: 'top-center',
      }
    );
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };
  return (
    <button
      className={`${copied ? 'bg-green-600' : 'bg-black'} absolute p-4 ${
        multiline
          ? 'rounded-lg h-auto bottom-2 right-2'
          : 'top-0 h-full right-0 rounded-r-lg'
      } text-white`}
      type="button"
      disabled={copied}
      onClick={copy}
    >
      {copied ? <IoCheckboxOutline /> : <IoCopy />}
    </button>
  );
};

const TextInput: React.FC<Props> = React.memo(
  ({
    showCopy,
    wrapperClassName,
    labelClassName,
    label,
    id,
    placeholder,
    type,
    value: valueProps,
    helperText,
    name,
    customChange,
    disabled,
    className,
    errorClassName,
    multiline,
    error,
    onChange,
    rows,
    readOnly,
    appendStyles,
    inputHeight
  }) => {
    const [value, setValue] = React.useState(valueProps || '');
    const [formType, setFormType] = React.useState<string>(type || 'text');

    React.useEffect(() => {
      setValue(valueProps);
    }, [valueProps]);

    const handleChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setValue(event.target.value);
      !!customChange && customChange(event.target.value);
    };
    const errorClass = 'border border-red-600';
    const defaulClass = `p-4 border border-gray-400 rounded-md ${
      error ? errorClass : ''
    }`;

    let customClass = className
      ? `${className} ${error ? errorClass : ''}`
      : '';
    customClass = appendStyles ? `${className} ${defaulClass}` : customClass;

    return (
      <div className={wrapperClassName}>
        {!!label && (
          <label
            className={`${labelClassName} flex justify-between sm:justify-start`}
            htmlFor={id}
          >
            {label}{' '}
            {helperText && (
              <Dropdown
                buttonClassName="p-0 ml-1"
                hideCaret
                toggleContent={() => (
                  <FaInfoCircle className="text-xs text-gray-400" />
                )}
              >
                <div className="text-sm font-medium p-3 w-80">{helperText}</div>
              </Dropdown>
            )}
          </label>
        )}
        <div className={`w-full relative ${inputHeight ? "h-auto" : "h-full"}`}>
          {multiline ? (
            <textarea
              id={id}
              name={name}
              className={customClass || defaulClass}
              placeholder={placeholder}
              readOnly={readOnly}
              disabled={disabled}
              value={onChange ? valueProps : value}
              onChange={(onChange as any) || handleChange}
              rows={rows}
            />
          ) : (
            <input
              id={id}
              name={name}
              className={customClass || defaulClass}
              type={formType || 'text'}
              placeholder={placeholder}
              readOnly={readOnly}
              disabled={disabled}
              value={onChange ? valueProps : value}
              onChange={onChange || handleChange}
            />
          )}
          {type === 'password' && (
            <button
              title="Show Password"
              onClick={() =>
                setFormType(formType === 'password' ? 'text' : 'password')
              }
              className="absolute bg-gray-100 rounded right-4 top-1/4 p-2"
              type="button"
            >
              {formType === 'password' ? <FiEye /> : <FiEyeOff />}
            </button>
          )}
          {showCopy && <CopyButton multiline={multiline} value={value} />}
        </div>

        {error?.message && (
          <span
            className={`${
              error?.message ? 'text-red-600' : 'text-gray-600'
            } ${errorClassName} text-xs`}
          >
            {error.message}
          </span>
        )}
      </div>
    );
  }
);

export default TextInput;
